import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Updater } from "use-immer";

import { IgDivider, IgRadio, IgTypography } from "@intelligems/ig-theme";
import { GenericExperienceFe } from "@intelligems/ig-types";
import { FormControlLabel, Grid, List, ListItem } from "@mui/material";
import {
  getNonNativeModsRollout,
  modLabelKey,
} from "@src/features/sharedExperience/components/rollouts/utils";
import { selectedAudience } from "@src/store/slices/audiences";
import { selectedRedirects } from "@src/store/slices/redirects";
import { theme } from "@src/theme";
import { personalizationRollingOutTests } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

interface RolloutsModificationConfirmProps {
  experience: GenericExperienceFe;
  ignoreOtherChanges: boolean | null;
  setIgnoreOtherChanges: Updater<boolean | null>;
  selectedVariationId: string;
}

export const RolloutsModificationConfirm = ({
  experience,
  ignoreOtherChanges,
  setIgnoreOtherChanges,
  selectedVariationId,
}: RolloutsModificationConfirmProps) => {
  const redirects = useSelector(selectedRedirects);
  const audience = useSelector(selectedAudience);

  const modsToRollout = useMemo(() => {
    return getNonNativeModsRollout(
      experience.testTypes,
      Boolean(audience?.filters?.length > 0) || false
    );
  }, [experience.testTypes]);

  const foundRedirectInfo = useMemo(() => {
    if (experience.testTypes.hasTestContentTheme) {
      return redirects.find(
        (redirect) => redirect.variationId === selectedVariationId
      )?.queryParams[0].value;
    }
    return null;
  }, [redirects, experience.testTypes, selectedVariationId]);

  useEffect(() => {
    if (ignoreOtherChanges === null) {
      setIgnoreOtherChanges(true);
    }
  }, [ignoreOtherChanges]);

  return (
    <Grid container item direction={"column"} rowSpacing={2}>
      <Grid item>
        <IgTypography>
          <IgTypography igStyle={"semibold"}>
            Theme {foundRedirectInfo}{" "}
          </IgTypography>{" "}
          will be applied as the live theme in Shopify
        </IgTypography>
      </Grid>
      <Grid item>
        <IgTypography>
          Your test contains {modsToRollout.length > 1 ? "" : "one"} additional
          change{modsToRollout.length > 1 ? "s" : ""} that we can't apply
          directly in Shopify:
        </IgTypography>
        <List sx={{ listStyleType: "disc", marginLeft: "20px" }}>
          {modsToRollout.map((val, i) => (
            <ListItem
              disableGutters={true}
              sx={{ display: "list-item" }}
              key={`mod-${i}`}
            >
              <IgTypography igStyle={"semibold"}>
                {modLabelKey[val]}
              </IgTypography>
            </ListItem>
          ))}
        </List>
      </Grid>
      <Grid item>
        <IgTypography>
          What would you like to do with these changes?
        </IgTypography>
      </Grid>
      <Grid item container direction={"column"}>
        <Grid item>
          <FormControlLabel
            value={true}
            control={
              <IgRadio
                checked={ignoreOtherChanges === true}
                onClick={() => {
                  setIgnoreOtherChanges(true);
                }}
              />
            }
            label={
              <IgTypography>
                Ignore them - I don’t need them, or I’ll apply them myself in
                Shopify
              </IgTypography>
            }
          />
        </Grid>
        <IgDivider spacing={"s"} />
        <Grid item>
          <FormControlLabel
            value={false}
            control={
              <IgRadio
                checked={ignoreOtherChanges === false}
                onClick={() => {
                  setIgnoreOtherChanges(false);
                }}
              />
            }
            label={
              <Grid container direction={"column"}>
                <IgTypography>Roll them out as an Experience</IgTypography>
                <IgTypography sx={{ color: theme.palette.text.secondary }}>
                  Intelligems shows them to your selected audience for as long
                  as you’d like.{" "}
                  <IgTypography
                    igStyle={"linkNoBold"}
                    onClick={() => {
                      openNewTab(
                        personalizationRollingOutTests,
                        "Rolling Out Tests" // check that this is the correct mixpanel tracker
                      );
                    }}
                  >
                    Find out more.
                  </IgTypography>
                </IgTypography>
              </Grid>
            }
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
